import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { getDispatchBoard, saveDispatch } from "@/lib/admin/production.functions";
import { printDispatchSheet } from "@/lib/admin/production-print";
import {
  DELIVERY_STATES,
  DELIVERY_STATE_LABEL,
  istToday,
  type DeliveryState,
} from "@/lib/admin/production.schemas";
import { orderSourceLabel } from "@/lib/admin/ops-sources";

export const Route = createFileRoute("/_admin/admin/deliveries")({
  head: () => ({
    meta: [
      { title: "Deliveries — Admin" },
      {
        name: "description",
        content: "Track out-for-delivery, delivered, failed and returned orders.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DeliveriesPage,
});

function DeliveriesPage() {
  const queryClient = useQueryClient();
  const [day, setDay] = useState(istToday());
  const [filter, setFilter] = useState("");

  const listFn = useServerFn(getDispatchBoard);
  const saveFn = useServerFn(saveDispatch);

  const board = useQuery({
    queryKey: ["admin", "deliveries", day],
    queryFn: () =>
      listFn({
        data: {
          from: day,
          to: day,
          statuses: ["out_for_delivery", "delivered", "failed", "cancelled"],
        },
      }),
    staleTime: 5_000,
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: (v: { orderId: string; state: DeliveryState }) =>
      saveFn({ data: { ...v, markDispatched: false } }),
    onSuccess: () => {
      toast.success("Delivery updated");
      void queryClient.invalidateQueries({ queryKey: ["admin", "deliveries"] });
    },
    onError: () => toast.error("Couldn't update the delivery."),
  });

  const all = board.data ?? [];
  const rows = filter ? all.filter((r) => r.delivery_state === filter) : all;
  const count = (s: DeliveryState) => all.filter((r) => r.delivery_state === s).length;

  return (
    <div>
      <AdminPageHeader
        title="Deliveries"
        description="Live delivery tracking for the selected service day."
        actions={
          <AdminDataToolbar
            exportName={`deliveries-${day}`}
            moduleId="dispatch"
            onImported={() =>
              void queryClient.invalidateQueries({ queryKey: ["admin", "deliveries"] })
            }
            rows={() =>
              rows.map((r) => ({
                "Order ID": r.id,
                "Order Ref": r.order_ref,
                Customer: r.customer_name,
                "Driver Name": r.driver_name,
                "Vehicle Number": r.vehicle_number,
                "Delivery State": r.delivery_state,
                "Dispatched At": r.dispatched_at ?? "",
                "Delivered At": r.delivered_at ?? "",
                Notes: r.notes,
              }))
            }
          />
        }
      />


      <RefreshBar
        targets={[{ key: "deliveries", label: "deliveries" }]}
        busy={board.isFetching}
        lastUpdated={board.dataUpdatedAt || null}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(["out_for_delivery", "delivered", "failed", "returned"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(filter === s ? "" : s)}
            className={`rounded-lg border p-3 text-left transition-colors ${
              filter === s ? "border-primary bg-muted" : "border-border hover:bg-muted/50"
            }`}
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {DELIVERY_STATE_LABEL[s]}
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{count(s)}</p>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value || istToday())}
          className="h-9 w-40"
          aria-label="Delivery date"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={rows.length === 0}
          onClick={() => printDispatchSheet(rows, day, "Delivery sheet")}
          className="gap-1.5"
        >
          <Printer className="h-3.5 w-3.5" aria-hidden />
          Print delivery sheet
        </Button>
      </div>

      {board.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : board.isError ? (
        <p role="alert" className="rounded-lg border border-destructive p-6 text-sm text-destructive">
          Deliveries could not be loaded. Refresh and try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          No deliveries match this selection.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Area</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Driver</th>
                <th className="px-3 py-2 text-left">Dispatched</th>
                <th className="px-3 py-2 text-left">Order status</th>
                <th className="px-3 py-2 text-left">Delivery state</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{r.order_ref}</td>
                  <td className="px-3 py-2">{r.customer_name}</td>
                  <td className="px-3 py-2">{r.neighbourhood || "—"}</td>
                  <td className="px-3 py-2">{orderSourceLabel(r.order_channel)}</td>
                  <td className="px-3 py-2">{r.driver_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.dispatched_at ? new Date(r.dispatched_at).toLocaleTimeString("en-IN") : "—"}
                  </td>
                  <td className="px-3 py-2">{r.status.replace(/_/g, " ")}</td>
                  <td className="px-3 py-2">
                    <select
                      value={r.delivery_state}
                      aria-label={`Delivery state for ${r.order_ref}`}
                      className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                      onChange={(e) =>
                        save.mutate({
                          orderId: r.id,
                          state: e.target.value as DeliveryState,
                        })
                      }
                    >
                      {DELIVERY_STATES.map((s) => (
                        <option key={s} value={s}>
                          {DELIVERY_STATE_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
